import gql from 'graphql-tag';
import className from 'classnames/bind';
import { InducteeSpotlightFragment } from '@graphqlTypes';
import { Button, Container, Image, ImageAttribution, RevealWrapper } from '@components';
import { ACFMediaFragment } from '@/fragments';
import { useRef } from 'react';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView, motion } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import styles from './Spotlight.module.scss';
const cx = className.bind(styles);

export default function Spotlight(props: InducteeSpotlightFragment) {

  const debug: boolean = false; // Set to true to enable debug styles!

  const {
    eyebrow,
    spotlightHeadline,
    spotlightBody,
    link,
    images,
    variant,
    mediaRight,
    imageAttribution,
    image
  } = props;

  const showLink = link && link.url && link.title;
  const [imageryVariant] = variant;
  const layoutDirection: 'left' | 'right' = mediaRight ? 'left' : 'right';

  // Temporary debug label // TODO: Remove later -Gabe
  if (debug) {
    console.warn('Spotlight debug mode is enabled');
    const debugLabel = `variant-${imageryVariant}`;
    document.documentElement.style.setProperty(
      '--spotlight-debug-label',
      `"${debugLabel}"`
    );
  }

  // -------------------- Animation

  const rootEl = useRef<HTMLDivElement>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  // -------------------- Image grid values

  const columns = [
    images?.slice(0, Math.ceil(images.length / 2)),
    images?.slice(Math.ceil(images.length / 2))
  ].filter(Boolean);

  // -------------------- Determine needed 'imagery' markup based on the layout variant

  function getVariantMarkup() {
    switch (imageryVariant) {

      // -------------------- Imagery Grid variant

      case 'imagery-grid': {
        return (
          <div className={cx("content__imagery")}>
            {columns.map((column, index) => (
              <div key={index} className={cx(['image-column'])}>
                {column.map(({ image, imageAttribution }, imgIndex) => (
                  <div key={imgIndex} className={cx("image-wrapper")}>
                    <Image image={image} />
                    {imageAttribution &&  <ImageAttribution text={imageAttribution} className={cx("spotlight-image-attribution")} /> }
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      }

      // -------------------- Imagery Single variant

      default:
      case 'imagery-single': {
        return (
          <div className={cx("content__imagery")}>
            <Image image={image} />
            {imageAttribution &&  <ImageAttribution text={imageAttribution} className={cx("spotlight-image-attribution")} /> }
          </div>
        );
      }

      // -------------------- Imagery Video variant

      case 'imagery-video': {
        return (
          <div className={cx("content__imagery")}>
            {/* <YoutubeImagePreview image={video.youtubeThumbnail} videoId={video.youtubeId} /> */}
          </div>
        );
      }

      // -------------------- Imagery stack variant (likely not needed)
      // TODO: Remove later if not used -Gabe
      // case 'image-stack': {
      //   return;
      // }

    }
  }

  // -------------------- Render

  return (
    <motion.section className={cx([
      'spotlight',
      `variant-${imageryVariant}`,
      `direction-${layoutDirection}`,
      debug ? 'debug' : ''
    ])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container className={cx("content")}>

          {/* Text content */}
          <div className={cx("content__text")}>

            {/* Title */}
            <div className={cx("title")}>
              <p className={cx(["eyebrow-l", "eyebrow"])}>{eyebrow}</p>
              <h2 className={cx("title-uc-l", "title__lines")}>
                {spotlightHeadline.map((line, index) => (
                  <span
                    key={index}
                    className={cx(['line', `title__lines__line-${index}`])}
                    dangerouslySetInnerHTML={{ __html: line.line }}
                  />
                ))}
              </h2>
            </div>

            {/* Body */}
            <div className={cx("body")}>
              {spotlightBody.map((line, index) => (
                <div className={cx("body__p-container")} key={index}>
                  <p className={"text-m"}>
                    {line.text}
                  </p>
                  { index === spotlightBody.length - 1 && showLink && (
                    <Button
                      label={link.title}
                      className={cx("link")}
                      url={link.url}
                      target={link.target}
                    />
                  )}
                </div>
              ))}
            </div>

          </div>

          {/* Imagery content */}
          {getVariantMarkup()}

        </Container>
      </RevealWrapper>
    </motion.section>
  );

}

Spotlight.fragments = {
  inducteeEntry: gql`
    ${ACFMediaFragment}
    fragment InducteeSpotlight on InducteeDataImpactFlexibleContentSpotlightLayout {
      eyebrow
      variant
      mediaRight: alignRight
      imageAttribution
      image {
        ...ACFMedia
      }
      video {
        youtubeId
        youtubeThumbnail {
          ...ACFMedia
        }
      }
      images {
        imageAttribution
        image {
          ...ACFMedia
        }
      }
      spotlightHeadline: headline {
        line
      }
      spotlightBody: body {
        text
      }
      link {
        title
        url
        target
      }
    }
  `,
};
